import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CreditCard } from 'lucide-react';

interface PaymentButtonProps {
  amount: number;
  paymentType: 'club_fee' | 'coach_clinic';
  recipientId: string;
  clubId?: string;
  organizationId?: string;
  metadata?: Record<string, string | number | boolean>;
  children?: React.ReactNode;
  variant?: 'default' | 'outline';
  size?: 'sm' | 'default' | 'lg';
}

export const PaymentButton: React.FC<PaymentButtonProps> = ({
  amount,
  paymentType,
  recipientId,
  clubId,
  organizationId,
  metadata = {},
  children,
  variant = 'default',
  size = 'default'
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handlePayment = async () => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('process-payment', {
        body: {
          payment_type: paymentType,
          amount: amount * 100, // Convert to cents
          recipient_id: recipientId,
          club_id: clubId,
          organization_id: organizationId,
          metadata
        }
      });

      if (error) throw error;

      if (data?.client_secret) {
        // In a real implementation, you would integrate with Stripe Elements here
        // For now, we'll show a success message
        toast({
          title: 'Payment Initiated',
          description: 'Payment processing has been started'
        });
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: 'Payment Failed',
        description: 'Unable to process payment. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handlePayment}
      disabled={isLoading}
      variant={variant}
      size={size}
      className="gap-2"
    >
      <CreditCard className="h-4 w-4" />
      {children || (isLoading ? 'Processing...' : `Pay $${amount}`)}
    </Button>
  );
};